export const CCP_API_PATHS = {
  /**
   * Contract chart details. Returns the security definition that should be used to query
   * chart data (`redirect`), the periods/steps the contract actually supports, and the
   * price magnifier to apply.
   *
   * This is the correct way to resolve HMDS query params: HMDS validates the
   * exchange/secType pair and rejects invalid combinations outright (e.g.
   * "Combination of secType and exchange is not allowed IND@SMART"), and some instruments
   * must be redirected to a different conid entirely for charting.
   *
   * `exchange` is optional — omitting it lets the service pick the preferred exchange.
   */
  CONTRACT_CHART_DETAILS: (params: { conid: number; exchange?: string }, usePublicApi = false) => {
    const base = usePublicApi ? 'public/ccpgw' : 'ccpgw';
    const queryParams = new URLSearchParams({ conid: String(params.conid) });
    if (params.exchange) queryParams.append('exchange', params.exchange);
    return `${base}/contract/chartDetails?${queryParams}`;
  },
};

export const HMDS_API_PATHS = {
  /**
   * Historical price bar/volume chart (HMDS "lastBar"). Returns OHLC + volume as
   * parallel arrays. Spec: https://confluence.core.svc.ibkr.com/display/BS/HMDS+Farm+Gateway+Service+-+Technical+Spec
   *
   * Combination of period and step must yield <= 1000 data points; with outsideRth
   * and a step < 1 day the cap is 3500. Requests over those are rejected by HMDS.
   *
   * Prefer sourcing conid/exchange/secType from CCP_API_PATHS.CONTRACT_CHART_DETAILS
   * (`redirect`) rather than assembling them, since HMDS rejects invalid pairs.
   */
  LAST_BAR: (
    params: {
      conid: number;
      exchange: string;
      secType: string;
      period?: string;
      step?: string;
      stepCount?: number;
      outsideRth?: boolean;
      to?: number;
    },
    usePublicApi = false,
  ) => {
    const base = usePublicApi ? 'public/hmds/lastBar' : 'hmds/lastBar';
    const queryParams = new URLSearchParams({
      conid: String(params.conid),
      exchange: params.exchange,
      secType: params.secType,
    });
    if (params.period) queryParams.append('period', params.period);
    if (params.step) queryParams.append('step', params.step);
    if (params.stepCount != null) queryParams.append('stepCount', String(params.stepCount));
    if (params.outsideRth != null) queryParams.append('outsideRth', String(params.outsideRth));
    if (params.to != null) queryParams.append('to', String(params.to));
    return `${base}?${queryParams}`;
  },
};

export const MDFARM_API_PATHS = {
  BASE_URL: 'mdfarm',
  EVENT_CONTRACT_MARKET_OPEN_INTEREST: (ids: string[], usePublicApi = false) => {
    const base = usePublicApi ? 'public' : 'mdfarm';
    const queryParams = new URLSearchParams();
    ids.forEach((id) => queryParams.append('id', id));
    return `${base}/event-contract/market-open-interest?${queryParams}`;
  },
  EVENT_CONTRACT_BID_ASK: (conid: number, exchange: string, usePublicApi = false) => {
    const base = usePublicApi ? 'public' : 'mdfarm';
    return `${base}/event-contract/bid-ask?conid=${conid}&exchange=${exchange}`;
  },
  EVENT_CONTRACT_FLEXBEST_BID_ASK: (conidExPairs: string[], usePublicApi = false) => {
    const base = usePublicApi ? 'public/mdfarm' : 'mdfarm';
    const queryParams = new URLSearchParams();
    conidExPairs.forEach((pair) => queryParams.append('conidEx', pair));
    return `${base}/event-contract/flexbest-bid-ask?${queryParams}`;
  },
  EVENT_CONTRACT_OPEN_INTEREST: (conid: number, exchange: string, usePublicApi = false) => {
    const base = usePublicApi ? 'public/mdfarm' : 'mdfarm';
    return `${base}/event-contract/open-interest?conid=${conid}&exchange=${exchange}`;
  },
  EVENT_CONTRACT_AGG_OPEN_INTEREST: (conidExPairs: string[], usePublicApi = false) => {
    const base = usePublicApi ? 'public/mdfarm' : 'mdfarm';
    const queryParams = new URLSearchParams();
    conidExPairs.forEach((pair) => queryParams.append('conidEx', pair));
    return `${base}/event-contract/agg-open-interest?${queryParams}`;
  },
};

export const NEWS2_API_PATHS = {
  BASE_URL: 'news2',
  ARTICLES_FEED: (params: {
    key: string;
    include?: string;
    max?: number;
    lang?: string;
    tz?: string;
    hybrid?: string;
    nav_anchor?: string;
  }) => {
    const queryParams = new URLSearchParams({
      key: params.key,
    });
    if (params.include) queryParams.append('include', params.include);
    if (params.max) queryParams.append('max', String(params.max));
    if (params.lang) queryParams.append('lang', params.lang);
    if (params.tz) queryParams.append('tz', params.tz);
    if (params.hybrid) queryParams.append('hybrid', params.hybrid);
    if (params.nav_anchor) queryParams.append('nav_anchor', params.nav_anchor);
    return `${NEWS2_API_PATHS.BASE_URL}/articles/feed?${queryParams}`;
  },
};

export const FORECASTTRADER_API_PATHS = {
  BASE_URL: (usePublicApi = false) => (usePublicApi ? 'public/forecasttrader' : 'forecasttrader'),
  MARKET_CATEGORY: (usePublicApi = false) => `${FORECASTTRADER_API_PATHS.BASE_URL(usePublicApi)}/market/category`,
  CONTRACT_MARKET: (
    params: { underlyingConid?: number; productConid?: number; exchange?: string },
    usePublicApi = false,
  ) => {
    const queryParams = new URLSearchParams();
    if (params.underlyingConid) queryParams.append('underlyingConid', String(params.underlyingConid));
    if (params.productConid) queryParams.append('productConid', String(params.productConid));
    if (params.exchange) queryParams.append('exchange', params.exchange);
    return `${FORECASTTRADER_API_PATHS.BASE_URL(usePublicApi)}/contract/market?${queryParams}`;
  },
  CONTRACT_SEARCH: (params: { prefix: string }, usePublicApi = false) => {
    const queryParams = new URLSearchParams({
      prefix: params.prefix,
    });
    return `${FORECASTTRADER_API_PATHS.BASE_URL(usePublicApi)}/contract/search?${queryParams}`;
  },
  CONTRACT_RULES: (params: { conid: number }, usePublicApi = false) => {
    const queryParams = new URLSearchParams({
      conid: String(params.conid),
    });
    return `${FORECASTTRADER_API_PATHS.BASE_URL(usePublicApi)}/contract/rules?${queryParams}`;
  },
  CONTRACT_DETAILS: (params: { conid: number }, usePublicApi = false) => {
    const queryParams = new URLSearchParams({
      conid: String(params.conid),
    });
    return `${FORECASTTRADER_API_PATHS.BASE_URL(usePublicApi)}/contract/details?${queryParams}`;
  },
  CONTRACT_MASS_DETAILS: (params: { conids: number[] }, usePublicApi = false) => {
    const queryParams = new URLSearchParams({
      conids: params.conids.join(','),
    });
    return `${FORECASTTRADER_API_PATHS.BASE_URL(usePublicApi)}/contract/mass/details?${queryParams}`;
  },
  ELECTION_MAP: (params: { office: string; raceType: string; party?: string }, usePublicApi = false) => {
    const queryParams = new URLSearchParams({
      office: params.office,
      raceType: params.raceType,
    });
    if (params.party) {
      queryParams.append('party', params.party);
    }
    return `${FORECASTTRADER_API_PATHS.BASE_URL(usePublicApi)}/election/map?${queryParams}`;
  },
  ELECTION_CONTRACTS: (params: { productConid: number; sortBy?: string }, usePublicApi = false) => {
    const queryParams = new URLSearchParams({
      productConid: String(params.productConid),
    });

    if (params.sortBy) {
      queryParams.set('sortBy', params.sortBy);
    }

    return `${FORECASTTRADER_API_PATHS.BASE_URL(usePublicApi)}/election/contracts?${queryParams}`;
  },
  ELECTION_GRID: (params: { office: string; raceType: string; party?: string }, usePublicApi = false) => {
    const queryParams = new URLSearchParams({
      office: params.office,
      raceType: params.raceType,
    });

    if (params.party) {
      queryParams.set('party', params.party);
    }

    return `${FORECASTTRADER_API_PATHS.BASE_URL(usePublicApi)}/election/grid?${queryParams}`;
  },
  ELECTION_STATE_GRID: (params: { offices?: string; raceType: string; party?: string }, usePublicApi = false) => {
    const parts = [`raceType=${encodeURIComponent(params.raceType)}`];
    if (params.offices) parts.push(`offices=${params.offices}`);
    if (params.party) parts.push(`party=${encodeURIComponent(params.party)}`);
    return `${FORECASTTRADER_API_PATHS.BASE_URL(usePublicApi)}/election/state/grid?${parts.join('&')}`;
  },
};

export const PREDICTION_API_PATHS = {
  BASE_URL: (usePublicApi = false) => (usePublicApi ? 'public/forecasttrader/prediction' : 'forecasttrader/prediction'),
  CATEGORY_TREE: (usePublicApi = false) => `${PREDICTION_API_PATHS.BASE_URL(usePublicApi)}/category/tree`,
  PRODUCT: (
    params: { endpoint: 'list' | 'popular' | 'featured'; categories?: string; sortBy?: string; office?: string },
    usePublicApi = false,
  ) => {
    const queryParams = new URLSearchParams();
    if (params.categories) queryParams.append('categories', params.categories);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.office) queryParams.append('office', params.office);
    const queryString = queryParams.toString();
    return `${PREDICTION_API_PATHS.BASE_URL(usePublicApi)}/product/${params.endpoint}${queryString ? `?${queryString}` : ''}`;
  },
  CONTRACT_LIST: (params: { conid: number; exchange: string }, usePublicApi = false) => {
    const queryParams = new URLSearchParams({
      conid: String(params.conid),
      exchange: params.exchange,
    });
    return `${PREDICTION_API_PATHS.BASE_URL(usePublicApi)}/contract/list?${queryParams}`;
  },
  PRODUCT_CONDITIONAL: (params: { conid: number; exchange: string }, usePublicApi = false) => {
    const queryParams = new URLSearchParams({
      conid: String(params.conid),
      exchange: params.exchange,
    });
    return `${PREDICTION_API_PATHS.BASE_URL(usePublicApi)}/product/conditional?${queryParams}`;
  },
  CONTRACT_CLOSING: (usePublicApi = false) => `${PREDICTION_API_PATHS.BASE_URL(usePublicApi)}/contract/closing`,
};

export const PNL_API_PATHS = {
  BASE_URL: 'pnl',
  ACCOUNT_POSITIONS: () => `${PNL_API_PATHS.BASE_URL}/account/positions`,
  // WebSocket endpoints for Account PnL subscriptions
  WS_ACCOUNT_SUBSCRIBE: '/account/subscribe',
  WS_ACCOUNT_UNSUBSCRIBE: '/account/unsubscribe',
};

export const ACES_API_PATHS = {
  BASE_URL: 'acesws',
  PM_LOGIN_DATA: () => `${ACES_API_PATHS.BASE_URL}/pmLoginData`,
};
